import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { SessionEventDto } from './dtos';

export const bookmarkEventKey = (event: SessionEventDto) => event.event_id !== undefined
    ? String(event.event_id) : JSON.stringify([event.session_id, event.occured_at]);

/** URL is the source of truth: reloads, shared links and history restore the same selection. */
export function useBookmarkValues(parameter: string) {
    const location = useLocation();
    const navigate = useNavigate();
    const valueSignature = JSON.stringify([...new Set(new URLSearchParams(location.search).getAll(parameter).filter(Boolean))]);
    const values = useMemo(() => JSON.parse(valueSignature) as string[], [valueSignature]);
    const setValues = useCallback((next: string[], replace = false) => {
        // Read the live search so independent controls changed in the same event do not overwrite each other.
        const params = new URLSearchParams(window.location.search);
        params.delete(parameter);
        [...new Set(next.filter(Boolean))].forEach(value => params.append(parameter, value));
        const search = params.toString();
        const target = `${location.pathname}${search ? `?${search}` : ''}${location.hash}`;
        if (target !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
            navigate(target, { replace, state: location.state });
        }
    }, [location.pathname, location.hash, location.state, navigate, parameter]);
    return [values, setValues] as const;
}

export function useBookmarkSelection(parameter: string) {
    const [values, setValues] = useBookmarkValues(parameter);
    const setValue = useCallback((next: string | null, replace = false) => setValues(next ? [next] : [], replace), [setValues]);
    return [values[0] ?? null, setValue] as const;
}

/** Resolves bookmarks independently of pagination, and keeps stale responses out of the current selection. */
export function useBookmarkItems<T>(parameter: string, load: ((key: string) => Promise<T>) | null, getKey: (item: T) => string) {
    const [keys, setKeys] = useBookmarkValues(parameter);
    const [cache, setCache] = useState<Map<string, T>>(new Map());
    const [error, setError] = useState('');
    const signature = JSON.stringify(keys);
    useEffect(() => {
        let active = true;
        setError('');
        if (load) {
            Promise.all(keys.filter(key => !cache.has(key)).map(async key => [key, await load(key)] as const)).then(entries => {
                if (active) setCache(current => new Map([...current, ...entries]));
            }).catch(failure => {
                if (active) setError(failure?.response?.data?.message || failure?.message || 'The bookmarked selection could not be loaded.');
            });
        }
        return () => { active = false; };
        // Selection is represented by its serialized keys, independent of array identities.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature, load]);
    const items = useMemo(() => keys.flatMap(key => cache.has(key) ? [cache.get(key)!] : []), [keys, cache]);
    const setItems: Dispatch<SetStateAction<T[]>> = useCallback(next => {
        const resolved = typeof next === 'function' ? next(items) : next;
        setCache(current => new Map([...current, ...resolved.map(item => [getKey(item), item] as const)]));
        setKeys(resolved.map(getKey));
    }, [getKey, items, setKeys]);
    return { items, setItems, keys, setKeys, error };
}

export function useBookmarkTab(names: readonly string[]) {
    const [tab, setTab] = useBookmarkSelection('tab');
    const index = Math.max(0, names.indexOf(tab ?? ''));
    return [index, (next: number) => setTab(names[next] ?? null)] as const;
}

export function useScopedBookmarkSelection(parameter: string, scope: string) {
    const [encoded, setEncoded] = useBookmarkSelection(parameter);
    let value: string | null = null;
    try {
        const decoded = JSON.parse(encoded ?? 'null');
        if (Array.isArray(decoded) && decoded[0] === scope && typeof decoded[1] === 'string') value = decoded[1];
    } catch { /* A malformed or differently scoped bookmark is not a selection. */ }
    return [value, (next: string | null) => setEncoded(next ? JSON.stringify([scope, next]) : null)] as const;
}
